// T3 Pareto front view. Self-contained: fetches the pareto_front endpoint and
// renders nothing for a single-objective session. For the common 2-objective
// case it draws a 2D SVG scatter (non-dominated front highlighted + connected,
// dominated members greyed); a table is always shown (scales to N objectives
// and gives the precise per-objective numbers). Display-only — scoring/steering
// stay with the rig (decoupled-reward). Polls live while the study runs.
import { useEffect, useRef, useState } from "react";

import {
  fetchParetoFront,
  type ParetoFrontResponse,
  type ParetoFrontMember,
} from "../api/auto_research";

export type ParetoFrontViewProps = {
  sessionId: number;
  // Poll the endpoint while the study is still running so the front grows live.
  live?: boolean;
  // Lifts each fetch's members up (e.g. for run-card "on front" badges) so the
  // parent shares this single fetch instead of polling the endpoint again.
  onMembers?: (members: ParetoFrontMember[]) => void;
};

const ACCENT = "#4a90d9";
const DOMINATED = "#777";
const pct = (v: number | null | undefined): string =>
  v == null ? "—" : `${(v * 100).toFixed(1)}%`;

function dirHint(direction: "min" | "max"): string {
  return direction === "min" ? "lower better" : "higher better";
}

export function ParetoFrontView({
  sessionId, live = false, onMembers,
}: ParetoFrontViewProps) {
  const [data, setData] = useState<ParetoFrontResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Ref so the latest callback is used without re-running the fetch effect.
  const onMembersRef = useRef(onMembers);
  onMembersRef.current = onMembers;

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const d = await fetchParetoFront(sessionId);
        if (!cancelled) {
          setData(d);
          setErr(null);
          onMembersRef.current?.(d.members);
        }
      } catch (e) {
        if (!cancelled) setErr(e instanceof Error ? e.message : String(e));
      }
    };
    load();
    const t = live ? window.setInterval(load, 4000) : undefined;
    return () => {
      cancelled = true;
      if (t !== undefined) window.clearInterval(t);
    };
  }, [sessionId, live]);

  if (err) {
    return (
      <section className="card" data-testid="pareto-front-error">
        <h4 style={{ marginTop: 0 }}>Pareto front</h4>
        <p style={{ color: "#ff9a9a", fontSize: 12 }}>Failed to load: {err}</p>
      </section>
    );
  }
  // Single-objective session (or nothing yet) → render nothing.
  if (!data || !data.is_pareto) return null;

  const { objectives, members } = data;
  const frontCount = members.filter((m) => m.on_front).length;
  // Front members first, then dominated; within each, sorted by the primary.
  const primary = objectives[0]?.id;
  const sorted = [...members].sort((a, b) => {
    if (a.on_front !== b.on_front) return a.on_front ? -1 : 1;
    const av = a.scores[primary] ?? 0;
    const bv = b.scores[primary] ?? 0;
    return av - bv;
  });
  // Best front member on each objective (the corners of the front).
  const bests = objectives.map((o) => {
    const better = (a: number, b: number) =>
      o.direction === "max" ? a > b : a < b;
    let best: ParetoFrontMember | null = null;
    for (const m of members) {
      const v = m.scores[o.id];
      if (!m.on_front || v == null) continue;
      const bv = best?.scores[o.id];
      if (best == null || (bv != null && better(v, bv))) best = m;
    }
    return { o, best };
  });

  return (
    <section className="card" data-testid="pareto-front-view">
      <h4 style={{ marginTop: 0 }}>
        Pareto front{" "}
        <span className="muted" style={{ fontSize: 12, fontWeight: 400 }}>
          {frontCount} non-dominated of {members.length} feasible
          {live ? " · live" : ""}
        </span>
      </h4>

      <div style={{ fontSize: 11, marginBottom: 6 }} data-testid="pareto-bests">
        {bests.map(({ o, best }) => (
          <span key={o.id} style={{ marginRight: 14, color: "#bcd" }}>
            best {o.display_name}:{" "}
            {best ? `R${best.run_number ?? "?"} ${pct(best.scores[o.id])}` : "—"}
          </span>
        ))}
      </div>

      {objectives.length === 2 ? (
        <ParetoScatter objectives={objectives} members={members} />
      ) : (
        <p className="muted" style={{ fontSize: 11 }}>
          {objectives.length} objectives — see the table below (scatter shown
          for 2-objective studies).
        </p>
      )}

      <table
        data-testid="pareto-front-table"
        style={{ width: "100%", fontSize: 12, borderCollapse: "collapse", marginTop: 8 }}
      >
        <thead>
          <tr style={{ textAlign: "left", color: "#9a9a9a" }}>
            <th>run</th>
            {objectives.map((o) => (
              <th key={o.id} title={`${o.id} (${dirHint(o.direction)})`}>
                {o.display_name}
              </th>
            ))}
            <th>front</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((m) => (
            <tr
              key={m.run_id}
              style={{ color: m.on_front ? undefined : "#9a9a9a" }}
            >
              <td>R{m.run_number ?? "?"}</td>
              {objectives.map((o) => (
                <td key={o.id}>{pct(m.scores[o.id])}</td>
              ))}
              <td style={{ color: m.on_front ? ACCENT : "#666" }}>
                {m.on_front ? "◆ on front" : "dominated"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted" style={{ fontSize: 11, marginTop: 4 }}>
        Scores are baseline-relative (vs R0). A member is on the front when no
        other feasible member beats it on every objective at once.
      </p>
    </section>
  );
}

// ---- 2D scatter (2 objectives) ----

type ScatterProps = {
  objectives: ParetoFrontResponse["objectives"];
  members: ParetoFrontMember[];
};

function ParetoScatter({ objectives, members }: ScatterProps) {
  const W = 380;
  const H = 280;
  const padL = 48;
  const padB = 36;
  const padT = 12;
  const padR = 14;
  const [ox, oy] = [objectives[0], objectives[1]];

  const pts = members
    .map((m) => ({
      m,
      x: m.scores[ox.id],
      y: m.scores[oy.id],
    }))
    .filter((p): p is { m: ParetoFrontMember; x: number; y: number } =>
      p.x != null && p.y != null,
    );
  if (pts.length === 0) return null;

  // Include the baseline (0,0) reference in the domain.
  const xs = [0, ...pts.map((p) => p.x)];
  const ys = [0, ...pts.map((p) => p.y)];
  const pad = (lo: number, hi: number) => {
    const span = hi - lo || Math.abs(hi) || 1;
    return [lo - span * 0.1, hi + span * 0.1] as const;
  };
  const [x0, x1] = pad(Math.min(...xs), Math.max(...xs));
  const [y0, y1] = pad(Math.min(...ys), Math.max(...ys));
  const sx = (v: number) => padL + ((v - x0) / (x1 - x0)) * (W - padL - padR);
  const sy = (v: number) => H - padB - ((v - y0) / (y1 - y0)) * (H - padT - padB);

  const front = pts
    .filter((p) => p.m.on_front)
    .sort((a, b) => a.x - b.x);
  const frontPath = front.map((p) => `${sx(p.x)},${sy(p.y)}`).join(" ");

  return (
    <svg
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      data-testid="pareto-scatter"
      role="img"
      aria-label={`Pareto scatter: ${ox.display_name} vs ${oy.display_name}`}
      style={{ maxWidth: W }}
    >
      {/* axes */}
      <line x1={padL} y1={H - padB} x2={W - padR} y2={H - padB} stroke="#444" />
      <line x1={padL} y1={padT} x2={padL} y2={H - padB} stroke="#444" />
      {/* baseline (R0) crosshair at 0,0 */}
      <line x1={sx(0)} y1={padT} x2={sx(0)} y2={H - padB} stroke="#333" strokeDasharray="3 3" />
      <line x1={padL} y1={sy(0)} x2={W - padR} y2={sy(0)} stroke="#333" strokeDasharray="3 3" />
      <text x={sx(0) + 3} y={sy(0) - 3} fill="#666" fontSize={9}>R0</text>
      {/* front polyline */}
      {front.length >= 2 && (
        <polyline points={frontPath} fill="none" stroke={ACCENT} strokeWidth={1.5} opacity={0.6} />
      )}
      {/* points */}
      {pts.map((p) => (
        <g key={p.m.run_id}>
          <circle
            cx={sx(p.x)}
            cy={sy(p.y)}
            r={p.m.on_front ? 5 : 3}
            fill={p.m.on_front ? ACCENT : DOMINATED}
            opacity={p.m.on_front ? 1 : 0.6}
          >
            <title>
              R{p.m.run_number ?? "?"} · {ox.display_name} {pct(p.x)} · {oy.display_name} {pct(p.y)}
              {p.m.on_front ? " · on front" : " · dominated"}
            </title>
          </circle>
          {p.m.on_front && (
            <text x={sx(p.x) + 6} y={sy(p.y) + 3} fill="#cdd" fontSize={9}>
              R{p.m.run_number ?? "?"}
            </text>
          )}
        </g>
      ))}
      {/* axis labels */}
      <text x={(padL + W - padR) / 2} y={H - 6} fill="#9a9a9a" fontSize={10} textAnchor="middle">
        {ox.display_name} ({dirHint(ox.direction)})
      </text>
      <text
        x={12}
        y={(padT + H - padB) / 2}
        fill="#9a9a9a"
        fontSize={10}
        textAnchor="middle"
        transform={`rotate(-90 12 ${(padT + H - padB) / 2})`}
      >
        {oy.display_name} ({dirHint(oy.direction)})
      </text>
    </svg>
  );
}
